import {
  Expander,
  ExpanderChevronPosition,
  ExpanderTitleRendererParams,
} from "@amzn/stencil-react-components/expander";
import { Col, Hr } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useCallback } from "react";
import { useIntl } from "react-intl";

import { ContentFeedback } from "@/components/GetSupport/SelfHelpFAQ/FAQ/List/ContentFeedback";
import { FAQItem } from "@/helpers/faq-helper";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import { StyledClickableH3 } from "./StyledClickableH3";

interface FAQItemContentProps {
  item: FAQItem;
}

/**
 * Literal UI/product names injected into answers so translation can't alter them.
 */
const ANSWER_VALUES = {
  orcaCorpText: "Orca-Corp-VPN",
  orcaText: "orca-Amazon-Onboarding",
  ciscoVpnText: "Cisco Secure Client VPN",
};

export const FAQItemContent = ({ item }: FAQItemContentProps) => {
  const intl = useIntl();

  const onToggle = useCallback(
    (isExpanded: boolean) => {
      if (isExpanded) {
        trackStructEvent({
          category: Day1EventCategory.FAQ,
          action: Day1EventAction.ExpandingFAQ,
          label: item.dataTestId.toUpperCase(),
        });
      }
    },
    [item]
  );

  const renderTitle = useCallback(
    ({ titleText, toggle }: ExpanderTitleRendererParams) => (
      <StyledClickableH3 fontWeight="normal" onClick={toggle}>
        {titleText}
      </StyledClickableH3>
    ),
    []
  );

  return (
    <Col className={"faq-list-content"}>
      <Hr color="neutral20" size="narrow" />
      <Expander
        dataTestId={item.dataTestId}
        titleText={intl.formatMessage({ id: item.questionId })}
        chevronPosition={ExpanderChevronPosition.Leading}
        shouldExpandOnMount={false}
        renderTitle={renderTitle}
        onToggle={onToggle}
      >
        <Col padding="S300">
          <Text fontSize="T100" color="color.text.secondary">
            {intl.formatMessage({ id: item.answerId }, ANSWER_VALUES)}
          </Text>
        </Col>
        <Col padding="S300">
          <ContentFeedback dataTestId={item.dataTestId} />
        </Col>
      </Expander>
    </Col>
  );
};
